import { IPolicyTerm, PolicyTermType } from "@app-rest/common.interfaces";
import useCompanyRestClient from "@app-rest/company";
import useSystemRestClient from "@app-rest/systems";
import { IGenericPage } from "@app-rest/systems.interfaces";
import useAppStore from "@app-store/app";
import useCompanyStore from "@app-store/company";
import useSystemStore from "@app-store/system";
import { camelCase, flow, upperFirst } from "lodash-es";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
	name: "PolicyTermPage",
	setup() {
		const route = useRoute();
		const type = computed(() => {
			return flow(upperFirst)(
				camelCase(route.params.type.toString())
			) as PolicyTermType;
		});
		const data = ref<Partial<IPolicyTerm> | IGenericPage>({});

		const systemStore = useSystemStore();
		const companyStore = useCompanyStore();
		const systemInfoId = systemStore.info?.Id;
		const restClient = useSystemRestClient();

		const getPolicyTerm = async() => {
			switch (type.value) {
				case PolicyTermType.CookiePolicy: {
					if (!systemInfoId) return null;
					const response = await restClient.templateCookiePage(systemInfoId);
					return response.data;
				}
				case PolicyTermType.PrivacyPolicy: {
					const restClient = useCompanyRestClient();
					const response = await restClient.getPrivacyPolicy(companyStore.Id);
					return response.data;
				}
				case PolicyTermType.PhotoPrivacyPolicy:
				case PolicyTermType.PhotoTerms: {
					await systemStore.ensurePhotoPrivacyAndPolicy();
					if (!systemStore.photoPrivacyAndTerms) return null;
					if (type.value === PolicyTermType.PhotoPrivacyPolicy) {
						return {
							IsEnabled: systemStore.photoPrivacyAndTerms.IsActive,
							UrlLabel: systemStore.photoPrivacyAndTerms.PrivacyPolicy.Label,
							Title: systemStore.photoPrivacyAndTerms.PrivacyPolicy.Title,
							Content: systemStore.photoPrivacyAndTerms.PrivacyPolicy.Description
						} satisfies IPolicyTerm;
					} else {
						return {
							IsEnabled: systemStore.photoPrivacyAndTerms.IsActive,
							UrlLabel: systemStore.photoPrivacyAndTerms.TermsOfService.Label,
							Title: systemStore.photoPrivacyAndTerms.TermsOfService.Title,
							Content: systemStore.photoPrivacyAndTerms.TermsOfService.Description
						} satisfies IPolicyTerm;
					}
				}
				case PolicyTermType.TermsOfUse: {
					if (!systemInfoId) return null;
					const response = await restClient.getTermsOfUse(systemInfoId);
					return response.data;
				}
				case PolicyTermType.TermsAndConditions: {
					if (!systemInfoId) return null;
					const response = await restClient.getTermsAndConditions(systemInfoId);
					return response.data;
				}
				default:
					return null;
			}
		};
		const loadContent = async() => {
			const response = await getPolicyTerm();
			data.value = response ?? {};
		};
		watch(
			() => type.value,
			async() => await loadContent()
		);
		onMounted(async() => {
			await loadContent();
			useAppStore().setLoadingView(false);
		});
		return {
			type,
			data
		};
	}
});
